import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const HeaderBackLinkContext = createContext();

const OVERRIDE_ACTIVE_KEY = 'header_backlink_override_active';
const OVERRIDE_LINK_KEY = 'header_backlink_override_link';
const OVERRIDE_FROM_PATH_KEY = 'header_backlink_override_from_path';


const SPECIAL_ROUTES = [
    // Format: { pattern: [segment conditions], target: '/target/path' }
    {
        // This matches: /program/:programId/anything
        pattern: ['program', '*', '*'],  // * means "any segment value"
        target: '/program'
    },
    {
        pattern: ['support', 'view-ticket', '*'],
        target: '/support'
    },
    {
        pattern: ['learner-progress', '*', '*'],
        target: '/learners'
    },
    {
        pattern: ['course', '*'],
        target: '/discover'
    },
    {
        pattern: ['program', '*'],
        target: '/discover'
    }
];

/**
 * Provider component for header back link functionality
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} Context provider with children
 */
export function HeaderBackLinkProvider({ children }) {
    const [backLink, setBackLink] = useState(null);
    const location = useLocation();
    const currentPath = location.pathname;

    useEffect(() => {
        console.log("Back link effect running for path:", currentPath);

        //? 1. Check for active override from sessionStorage
        const hasOverride = sessionStorage.getItem(OVERRIDE_ACTIVE_KEY) === 'true';
        const overrideLink = sessionStorage.getItem(OVERRIDE_LINK_KEY);
        const overrideFromPath = sessionStorage.getItem(OVERRIDE_FROM_PATH_KEY);

        //? 2. If override exists and we've navigated to a new page, use the override
        if (hasOverride && overrideFromPath && overrideFromPath !== currentPath) {
            console.log("Using manual override back link:", overrideLink);
            setBackLink(overrideLink);

            //? 3. Clear the override after using it once
            sessionStorage.removeItem(OVERRIDE_ACTIVE_KEY);
            sessionStorage.removeItem(OVERRIDE_LINK_KEY);
            sessionStorage.removeItem(OVERRIDE_FROM_PATH_KEY);

            return;
        }

        //? 4. If no override or same path as override, calculate automatically
        calculateBackLink(currentPath);

    }, [currentPath]);

    const calculateBackLink = (path) => {
        console.log("Calculating automatic back link for:", path);
        const segments = path.split('/').filter(Boolean);

        //? 5. Check for special route patterns
        for (const route of SPECIAL_ROUTES) {
            if (segments.length >= route.pattern.length) {
                let match = true;

                for (let i = 0; i < route.pattern.length; i++) {
                    if (route.pattern[i] !== '*' && route.pattern[i] !== segments[i]) {
                        match = false;
                        break;
                    }
                }

                if (match) {
                    console.log("Special route match found, setting back link to:", route.target);
                    setBackLink(route.target);
                    return;
                }
            }
        }

        //? 6. No back button for root routes
        if (segments.length <= 1) {
            console.log("Root route detected, clearing back link");
            setBackLink(null);
            return;
        }

        //? 7. Default behavior: go up one level in URL hierarchy
        segments.pop();
        const newBackLink = `/${segments.join('/')}`;
        console.log("Setting default back link to parent route:", newBackLink);
        setBackLink(newBackLink);
    };

    const setBackLinkWithOverride = (link) => {
        console.log("Setting manual back link override to:", link);

        if (link && link.includes("/")) {
            //? 8. Set in state for immediate use
            setBackLink(link);

            //? 9. Store in sessionStorage for persistence between routes
            sessionStorage.setItem(OVERRIDE_ACTIVE_KEY, 'true');
            sessionStorage.setItem(OVERRIDE_LINK_KEY, link);
            sessionStorage.setItem(OVERRIDE_FROM_PATH_KEY, currentPath);
        } else {
            //? 10. Clear override if invalid link
            setBackLink(null);
            sessionStorage.removeItem(OVERRIDE_ACTIVE_KEY);
            sessionStorage.removeItem(OVERRIDE_LINK_KEY);
            sessionStorage.removeItem(OVERRIDE_FROM_PATH_KEY);
        }
    };

    return (
        <HeaderBackLinkContext.Provider value={{
            backLink,
            setBackLink: setBackLinkWithOverride,
        }}>
            {children}
        </HeaderBackLinkContext.Provider>
    );
}

/**
 * Hook to access and manage header back link state
 * @returns {Object} Header back link context
 * @property {string|null} backLink - The current back link URL
 * @property {Function} setBackLink - Function to manually set the back link URL (overrides automatic behavior)
 */
export function useHeaderBackLink() {
    return useContext(HeaderBackLinkContext);
}

export default useHeaderBackLink;